/**
 * PATH: apps/qa/src/pages/new-inspection/SidePanel.tsx
 * PURPOSE: Live preview sidebar for wizard steps 1-3
 */

import { Building2, CheckCircle2, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CATEGORY_INFO, PROJECT_TYPE_INFO } from '@/lib/inspection-constants'
import type { Job, FloorDraft } from './inspection-types'

interface SidePanelProps {
  selectedJob: Job | null
  inspectionName: string
  category: 'residential' | 'commercial' | 'infrastructure'
  projectType: 'new_construction' | 'renovation' | 'remediation'
  buildingClasses: string[]
  tradeTags: string[]
  floors: FloorDraft[]
  totalAreas: number
  completionProgress: number
}

export default function SidePanel(props: SidePanelProps) {
  const {
    selectedJob, inspectionName, category, projectType,
    buildingClasses, tradeTags, floors, totalAreas, completionProgress,
  } = props

  return (
    <div className="hidden lg:block">
      <div className="bg-card rounded-xl shadow-sm border border-border p-4 sticky top-6">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-primary" />
          <h3 className="font-medium text-foreground text-sm">Live Preview</h3>
        </div>

        {/* Completion Progress */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-muted-foreground">Completion</span>
            <span className={cn('font-medium', completionProgress === 100 ? 'text-emerald-500' : 'text-foreground')}>{completionProgress}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div className={cn('h-full transition-all duration-300', completionProgress === 100 ? 'bg-emerald-500' : 'bg-primary')} style={{ width: `${completionProgress}%` }} />
          </div>
        </div>

        {/* Quick Summary */}
        <div className="space-y-3 text-sm">
          <CheckItem done={!!selectedJob}>{selectedJob ? `Job #${selectedJob.job_id}` : 'No job selected'}</CheckItem>
          <CheckItem done={!!inspectionName.trim()}>{inspectionName.trim() || 'No name'}</CheckItem>
          <CheckItem done={buildingClasses.length > 0}>{buildingClasses.length > 0 ? `${buildingClasses.length} building class${buildingClasses.length !== 1 ? 'es' : ''}` : 'No classes'}</CheckItem>
          <CheckItem done={tradeTags.length > 0}>{tradeTags.length > 0 ? `${tradeTags.length} trade${tradeTags.length !== 1 ? 's' : ''}` : 'No trades'}</CheckItem>
          <CheckItem done={floors.length > 0}>{floors.length > 0 ? `${floors.length} floor${floors.length !== 1 ? 's' : ''}, ${totalAreas} areas` : 'No structure'}</CheckItem>
        </div>

        {/* Category Badge */}
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center gap-2">
            {(() => { const CategoryIcon = CATEGORY_INFO[category]?.icon || Building2; return <CategoryIcon className="w-4 h-4 text-primary" /> })()}
            <span className="text-sm text-foreground">{CATEGORY_INFO[category]?.name}</span>
            <span className="text-muted-foreground">•</span>
            <span className="text-sm text-muted-foreground">{PROJECT_TYPE_INFO[projectType]?.name}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function CheckItem({ done, children }: { done: boolean; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      {done ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <div className="w-4 h-4 rounded-full border-2 border-muted-foreground" />}
      <span className={cn(done ? 'text-foreground' : 'text-muted-foreground', 'truncate')}>{children}</span>
    </div>
  )
}
